<script setup lang="ts">
/**
 * The Billing screen (os#1615): the plan and its period, this period's usage of every billed unit against what the
 * plan includes, the payment methods on file, every invoice with its document, and the closed periods' usage against
 * what was reported to FloPay. A person holding `billing:write` (or an owner) also gets the controls that change the
 * plan and the card; one holding only `billing:read` sees the same page with none of them. The API holds the same line
 * whatever the screen shows.
 *
 * The plans are a row of cards that scrolls sideways instead of wrapping: each says what the plan includes and costs
 * at the interval chosen above the row, the one the enterprise is on is marked, and a link goes to the full pricing
 * page. Choosing a plan goes to FloPay's hosted checkout page for it where the deployment configures one, carrying the
 * person's email and the enterprise's signed reference; where it configures none, the first checkout happens here, without leaving
 * the portal: the invoice details in `BillingCheckoutForm`, the card in FloPay's own
 * fields inside it. Either way the plan moves when FloPay confirms the payment, never at the moment of checkout. A
 * plan staff price by hand is "talk to us", with the request form filled in for it.
 *
 * Usage is one meter for each of the five things a plan includes an amount of and bills past it: members, runner
 * minutes, portal runs, concurrent runs and storage. Members is the members the enterprise has now against the
 * plan's; the member-months the bill counts them in, by the day, are said in a line under the meters. Runner minutes
 * and portal runs are counted over the period and come with the overview; concurrent runs (#1622) is the ceiling
 * against the plan's own figure, and storage (#1621) is the gigabytes held now against the plan's. Each of those two
 * is read on its own, so a read that fails costs its meter only.
 *
 * A payment that did not go through, and a plan that has been cancelled and when it ends, are each said in a panel
 * at the top of the page, in a sentence that says what happens next, as well as in the line under the title.
 *
 * Payment methods are shown once there is a subscription (or a card on file): a card cannot be added before the
 * first checkout makes the enterprise a customer. Adding a card and choosing which is charged are offered only where
 * the API says FloPay can do them (`overview.cards`); removing one always is.
 *
 * The Concurrent runs panel (#1622) follows the meters: the ceiling and what it is made of, the plan's price per slot, and on
 * a paid plan a number and a Save button that ask for that many extra slots, for whoever may write billing. The
 * ceiling shown is what FloPay last reported, so it moves when FloPay confirms, not when Save is pressed. Slots asked
 * for that FloPay bills already but has not confirmed are shown beside it, so the panel never hides what is being
 * billed.
 *
 * The Mail panel (#1623) sits between the meters and Concurrent runs: each member mailbox's inbound mail today against
 * the plan's daily cap, as plain text in the kit's panel, a mailbox at its cap marked "limit reached", and how many days
 * the plan keeps mail before deleting it. It is not a meter, because the cap is per mailbox and per day, not an amount
 * the plan includes for the period. Read on its own like storage, so a failed read costs this panel only and says why
 * with the page's other problems.
 */

import { paymentNotice } from '@zerohuman/shared'
import {
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    Input,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    ZhBanner,
    ZhButton,
    ZhCarousel,
    ZhConfirmButton,
    ZhEmptyState,
    ZhKeyValue,
    ZhPageTitle,
    ZhPanel,
    ZhPlanCard,
    ZhSectionHeading,
    ZhStatusPill,
    ZhUsageMeter,
} from '@zerohuman/ui'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
    api,
    type BillingBusinessDetails,
    type BillingInterval,
    type BillingInvoice,
    type BillingOverview,
    type BillingPaymentMethod,
    type BillingPlan,
    type BillingReconciliationRow,
    billingInvoicesExportUrl,
    billingUsageExportUrl,
    type ConcurrentSlotChange,
    type ConcurrentSlots,
    type KeptMembers,
    type MailUsage,
    type Member,
    type PendingPlanChange,
    type PlanChangePreview,
    type PlanChangePreviewItem,
    type StorageUsage,
} from '@/api'
import BillingCardFields from '@/components/BillingCardFields.vue'
import BillingCheckoutForm, { type CheckoutDetails } from '@/components/BillingCheckoutForm.vue'
import { BILLING_COUNTRIES, TAX_ID_COUNTRIES } from '@/lib/billingCountries'
import { SITE_ORIGIN } from '@/lib/docs'
import { memberLabel } from '@/lib/memberLabel'

/** Where "talk to us" goes: the same contact destination /pricing uses. */
const CONTACT_URL = `${SITE_ORIGIN}/request-access`
/** The budget band the request form starts on for a plan priced by hand: its top one. */
const CONTACT_BUDGET = 'over-1500'
/** Every plan side by side, in full. */
const PRICING_URL = `${SITE_ORIGIN}/pricing`

/** The unit members are billed in: a member for a month, counted by the day. */
const MEMBER_MONTH = 'member_month'

const UNIT_LABEL: Record<string, string> = {
    member_month: 'member-months',
    runner_minute: 'runner minutes',
    portal_run: 'portal runs',
    storage_gb: 'storage GB-months',
}

const route = useRoute()
const router = useRouter()
const overview = ref<BillingOverview | null>(null)
const invoices = ref<BillingInvoice[]>([])
const methods = ref<BillingPaymentMethod[]>([])
const reconciliation = ref<BillingReconciliationRow[]>([])
const canWrite = ref(false)
/** The enterprise signed into and the person's email: what a checkout or a "talk to us" request is attributed by. */
const enterprise = ref<{ id: string; slug: string } | null>(null)
const email = ref('')
const loading = ref(true)
const error = ref('')
const notice = ref('')

const checkoutPlan = ref<string | null>(null)
const annual = ref(false)
const fieldSession = ref<string | null>(null)
const paymentMethodId = ref<string | null>(null)
const busy = ref(false)
const checkoutError = ref('')

const business = ref<BillingBusinessDetails | null>(null)
const businessError = ref('')

const addingCard = ref(false)
const cardSession = ref<string | null>(null)
const cardError = ref('')

const storage = ref<StorageUsage | null>(null)
const storageError = ref('')

const mail = ref<MailUsage | null>(null)
const mailError = ref('')

const slots = ref<ConcurrentSlots | null>(null)
const slotsLoadError = ref('')
/**
 * The number in the control: what the subscription comes to once any scheduled reduction lands, else what was asked
 * and FloPay bills already, else the slots bought.
 */
const slotsWanted = ref<string | number>('')
const slotsError = ref('')
const slotsNote = ref('')
const saving = ref(false)

/** The downgrade pending for the period end (os#1616), and what each lower plan would shrink, by plan. */
const pendingPlan = ref<PendingPlanChange | null>(null)
const previews = ref<Record<string, PlanChangePreview>>({})
const planChangeError = ref('')
const planChangeNote = ref('')
const changingPlan = ref(false)

/**
 * Who stays active once the plan shrank to Free (os#1624): the members the plan holds paused, the members, and which
 * boxes are checked. A box is checked while the member would stay active; the COO always does.
 */
const kept = ref<KeptMembers | null>(null)
const keptPeople = ref<Member[]>([])
const keptChecked = ref<string[]>([])
const keptError = ref('')
/** Why who stays active could not be read: said with the page's other problems, since the panel needs that read. */
const keptLoadError = ref('')
const keptNote = ref('')
const savingKept = ref(false)

/** What each outcome of a plan that shrinks is called, and the status tone it takes. */
const OUTCOME: Record<
    PlanChangePreviewItem['outcome'],
    { word: string; tone: 'running' | 'action' | 'neutral' | 'failed' }
> = {
    keeps_running: { word: 'Keeps running', tone: 'running' },
    paused: { word: 'Paused', tone: 'action' },
    ends: { word: 'Ends', tone: 'neutral' },
    waits: { word: 'Waits', tone: 'neutral' },
    removed: { word: 'Removed', tone: 'failed' },
}

const PREVIEW_UNIT: Record<string, string> = {
    member: 'Members',
    schedule: 'Enabled schedules',
    concurrent_slot: 'Extra concurrent runs',
    concurrent_run: 'Concurrent runs',
    storage: 'Storage',
    execution_history: 'Execution history',
    mail: 'Mail',
}

const BILLED: Record<BillingInterval, string> = { monthly: 'Billed monthly', annual: 'Billed annually' }

/** What could not be read or done, each reason once: one refusal is often the answer to every read on the page. */
const problems = computed(() =>
    [
        ...new Set(
            [error.value, storageError.value, mailError.value, slotsLoadError.value, keptLoadError.value].filter(
                reason => reason !== '',
            ),
        ),
    ].join(' '),
)

/**
 * One line per member mailbox (#1623): "Ava: 14 of 20 today" against the plan's daily cap, or "Ava: 14 today, no
 * daily limit on Company" on a plan without one. A mailbox that has taken its cap says so, "Ava: 20 of 20 today,
 * limit reached", since mail to it is being bounced and the bounce goes only to the sender.
 */
const mailLines = computed(() => {
    const view = mail.value
    if (!view) return []
    return view.mailboxes.map(box => {
        const full = box.dailyCap !== null && box.inboundToday >= box.dailyCap
        return {
            memberId: box.memberId,
            full,
            text:
                box.dailyCap === null
                    ? `${box.memberName}: ${box.inboundToday} today, no daily limit on ${view.planName}`
                    : `${box.memberName}: ${box.inboundToday} of ${box.dailyCap} today${full ? ', limit reached' : ''}`,
        }
    })
})

/**
 * How long the plan keeps mail before the retention sweep deletes it (#1623): "Mail is kept for 7 days on Free, then
 * deleted". Empty when the read carries no window, so the panel never states a period it was not told.
 */
const mailRetentionLine = computed(() => {
    const view = mail.value
    if (!view || !(view.mailRetentionDays > 0)) return ''
    const days = view.mailRetentionDays === 1 ? '1 day' : `${view.mailRetentionDays} days`
    return `Received and sent mail is kept for ${days} on ${view.planName}, then deleted.`
})

/**
 * One meter per metered item, in the order a plan lists them: the units counted over the period, then concurrent runs
 * (the most that ran at once this period against the ceiling, the plan's figure plus slots bought), then storage (GB
 * held now, to 2 places, against the plan's). A part that could not be read has no meter. A peak at the ceiling reads
 * at: runs waited for a slot, which is when the enterprise needs more.
 */
const meters = computed(() => {
    const members = overview.value?.members
    const rows = (overview.value?.units ?? []).map(unit =>
        // Members reads as the members there are now; the member-months the bill counts are said under the meters.
        unit.unit === MEMBER_MONTH && typeof members === 'number'
            ? { key: unit.unit, used: members, included: unit.included, unit: 'members' }
            : { key: unit.unit, used: figure(unit.used), included: unit.included, unit: unitLabel(unit.unit) },
    )
    const peak = overview.value?.concurrentRunsPeak
    if (slots.value && typeof peak === 'number') {
        rows.push({
            key: 'concurrent_runs',
            used: peak,
            included: slots.value.ceiling,
            unit: 'concurrent runs at peak',
        })
    }
    if (storage.value) {
        rows.push({
            key: 'storage',
            used: Number(storage.value.heldGb.toFixed(2)),
            included: storage.value.includedGb,
            unit: 'GB of storage',
        })
    }
    return rows
})

/**
 * The closed periods the Export block offers (#398): one per period the reconciliation read has rows for, newest
 * first, each labelled by its dates as the Closed periods table labels it. The period in progress is not one: it has
 * no invoice to reconcile to yet.
 */
const exportPeriods = computed(() => {
    const periods = new Map<string, { start: string; end: string; label: string }>()
    for (const row of reconciliation.value) {
        if (!periods.has(row.periodStart)) {
            periods.set(row.periodStart, {
                start: row.periodStart,
                end: row.periodEnd,
                label: `${day(row.periodStart)} to ${day(row.periodEnd)}`,
            })
        }
    }
    return [...periods.values()].sort((a, b) => Date.parse(b.start) - Date.parse(a.start))
})

/** The start of the period picked in the Export block; until one is picked, the newest is the one exported. */
const exportPeriodPick = ref('')
const exportPeriod = computed(
    () => exportPeriods.value.find(period => period.start === exportPeriodPick.value) ?? exportPeriods.value[0] ?? null,
)

/**
 * Whether any listed invoice is dated in `period`, `[start, end)`, by the date the list shows: the rule the
 * invoices file narrows by (CTO decisions 6 and 14). When none is, the file holds its header row only, and the block
 * says so rather than hand over a blank sheet unexplained.
 */
function invoicesDatedIn(period: { start: string; end: string }): boolean {
    const from = Date.parse(period.start)
    const to = Date.parse(period.end)
    return invoices.value.some(invoice => {
        const at = Date.parse(invoice.date)
        return at >= from && at < to
    })
}

/** The Export block's select chose a period: its start, as the reconciliation read gives it. */
function pickExportPeriod(value: unknown): void {
    if (typeof value === 'string') {
        exportPeriodPick.value = value
    }
}

/** Said under the meters where the concurrent runs meter shows: what its peak is, and what reaching the ceiling means. */
const concurrencyLine = computed(() =>
    meters.value.some(meter => meter.key === 'concurrent_runs')
        ? 'Concurrent runs at peak is the most runs that ran at once this period. At the ceiling, new runs wait for a slot until one finishes: add slots or move up a plan to run more at once.'
        : '',
)

/**
 * "Members are counted by the day for the bill: 0.16 member-months so far this period." Said only where the members
 * meter shows the count, so the figure the bill uses is never lost.
 */
const memberMonthsLine = computed(() => {
    const unit = overview.value?.units.find(row => row.unit === MEMBER_MONTH)
    if (!unit || typeof overview.value?.members !== 'number') return ''
    return `Members are counted by the day for the bill: ${figure(unit.used)} member-months so far this period.`
})

/** "6 at once: 4 on Company + 2 extra", or "4 at once on Company" with none bought. */
const ceilingLine = computed(() => {
    const view = slots.value
    if (!view) return ''
    return view.slots > 0
        ? `${view.ceiling} at once: ${view.planConcurrentRuns} on ${view.planName} + ${view.slots} extra`
        : `${view.ceiling} at once on ${view.planName}`
})

/** The period end a reduction lands at, as the billing date it is: "5 Nov 2026". */
function billingDate(iso: string): string {
    return new Date(iso).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
    })
}

/**
 * Slots asked to apply now that FloPay bills already but has not confirmed: the ceiling above is still the count
 * FloPay last reported, and moves to the plan's figure plus these once it confirms. Null when nothing is awaiting it.
 */
const requested = computed(() => slots.value?.requested ?? null)

/** "Asked for 4 extra, billed from now. The ceiling rises to 8 once FloPay confirms it." */
const requestedLine = computed(() => {
    const view = slots.value
    const asked = requested.value
    if (!view || asked === null) return ''
    const moves = asked > view.slots ? 'rises' : 'falls'
    return `Asked for ${asked} extra, billed from now. The ceiling ${moves} to ${view.planConcurrentRuns + asked} once FloPay confirms it.`
})

/**
 * "Reducing to 1 extra from 5 Nov 2026, at the end of the period". The ceiling holds until then, unless slots asked
 * for are still awaiting FloPay: then it moves when FloPay confirms them, and the line above says so.
 */
const scheduledLine = computed(() => {
    const scheduled = slots.value?.scheduled
    if (!scheduled) return ''
    const when = scheduled.from
        ? `from ${billingDate(scheduled.from)}, at the end of the period`
        : 'at the end of the period'
    const meanwhile = requested.value === null ? ' Until then the ceiling stays as it is.' : ''
    return `Reducing to ${scheduled.slots} extra ${when}.${meanwhile}`
})

/**
 * What a save did, in words. `held` is the slots bought before the save: a change applied now for no more than that
 * raises nothing, and is the buyer keeping them, which withdraws the reduction that was scheduled.
 */
function noteFor(change: ConcurrentSlotChange, held: number): string {
    if (change.effective === 'now') {
        return change.slots > held
            ? `Asked for ${change.slots} extra. The ceiling rises once FloPay confirms it.`
            : `Keeping ${change.slots} extra. The scheduled reduction is cancelled once FloPay confirms it, and the ceiling stays as it is.`
    }
    if (change.effective === 'period_end') {
        return `Reducing to ${change.slots} extra at the end of the period.`
    }
    return 'Nothing to change.'
}

/** The plan the enterprise is on, by its product name where the API gives it. */
const currentPlanName = computed(() => {
    const plan = overview.value?.plan ?? ''
    return overview.value?.plans.find(candidate => candidate.planId === plan)?.name ?? planName(plan)
})

/**
 * What the owner needs to know about the subscription itself, each in one sentence that says what happens next: a
 * payment that did not go through, and a plan that has been cancelled.
 */
const subscriptionNotices = computed(() => {
    const notices: { key: string; text: string }[] = []
    const unpaid = paymentNotice({ pastDue: overview.value?.pastDue === true, plan: currentPlanName.value })
    if (unpaid) {
        notices.push({ key: 'past-due', text: unpaid.sentence })
    }
    if (overview.value?.cancelAt) {
        notices.push({
            key: 'cancelled',
            text: `The ${currentPlanName.value} plan has been cancelled and ends on ${billingDate(overview.value.cancelAt)}. Until then nothing changes; after that this company returns to Free.`,
        })
    }
    return notices
})

const interval = computed<BillingInterval>(() => (annual.value ? 'annual' : 'monthly'))
const canCheckout = computed(() => canWrite.value && overview.value?.subscribed !== true)
/** Whether any plan has a yearly price: the interval choice is offered only then. */
const sellsAnnual = computed(() => (overview.value?.plans ?? []).some(plan => plan.annual !== null))
/** A card can be added only once the first checkout has made the enterprise a customer. */
const showsPaymentMethods = computed(() => overview.value?.subscribed === true || methods.value.length > 0)
/** An API that does not say what can be done with a card is one from when both could. */
const canAddCard = computed(() => canWrite.value && overview.value?.cards?.canAdd !== false)
const canMakeDefault = computed(() => canWrite.value && overview.value?.cards?.canMakeDefault !== false)

/**
 * One card per plan on offer: its price at the interval chosen (a plan with no yearly price keeps its monthly one),
 * what it includes, and what a person may do with it. `hosted` is FloPay's checkout page for the plan, where the
 * deployment configures one; `talk` is the request form for a plan priced by hand. On a live subscription the plan
 * changes (os#1616) sit on the cards too: `upgrade` on a higher plan, `downgrade` on a lower one, and `cancel` on Free.
 */
const planCards = computed(() =>
    (overview.value?.plans ?? []).map(plan => {
        const current = plan.planId === overview.value?.plan
        const yearly = annual.value && plan.annual !== null
        const amount = yearly ? plan.annual : plan.monthly
        const sold = plan.selfServe && amount !== null
        // An API older than the plan cards says only "not self-serve": such a plan stays "talk to us".
        const byHand = !plan.selfServe && plan.custom !== false
        const at: BillingInterval = yearly ? 'annual' : 'monthly'
        const link = plan.checkoutLinks?.[at] ?? null
        return {
            planId: plan.planId,
            name: plan.name ?? planName(plan.planId),
            tagline: plan.tagline ?? '',
            current,
            ...planPrice(plan, yearly, byHand),
            features: includedLines(plan),
            choose: sold && canCheckout.value && !current,
            upgrade: isUpgrade(plan),
            downgrade: isDowngrade(plan),
            cancel: plan.planId === 'free' && canCancel.value,
            hosted: link ? hostedCheckout(link) : null,
            talk: byHand && !current ? contactUrl(plan.planId) : null,
        }
    }),
)

/**
 * What a plan's card says it costs: its price at the interval shown, or, with no price to choose, where it starts
 * (a plan priced by hand) or $0 (the free one).
 */
function planPrice(plan: BillingPlan, yearly: boolean, byHand: boolean): { price: string; per: string } {
    const amount = yearly ? plan.annual : plan.monthly
    if (plan.selfServe && amount !== null) {
        return { price: usd(amount), per: yearly ? 'a year' : 'a month' }
    }
    if (!byHand) {
        return { price: usd(0), per: 'a month' }
    }
    return plan.fromMonthly ? { price: `From ${usd(plan.fromMonthly)}`, per: 'a month' } : { price: 'Custom', per: '' }
}

/** What a plan includes, one line each, in the order a plan lists them. None from an API that does not say. */
function includedLines(plan: BillingPlan): string[] {
    const included = plan.included
    if (!included) return []
    return [
        `${count(included.members)} AI ${included.members === 1 ? 'member' : 'members'}`,
        `${count(included.runnerMinutes)} runner minutes a month`,
        `${count(included.portalRuns)} portal runs a month`,
        `${count(included.concurrentRuns)} concurrent ${included.concurrentRuns === 1 ? 'run' : 'runs'}`,
        `${count(included.storageGb)} GB of storage`,
    ]
}

/**
 * FloPay's hosted checkout page for a plan, as the API hands it over: already opened for this enterprise, by a
 * reference only the API can sign (`user_id`), so the payment finds its way back. The person's email is added here to
 * start the page on it.
 */
function hostedCheckout(link: string): string {
    const url = new URL(link)
    if (email.value) url.searchParams.set('email', email.value)
    return url.toString()
}

/** The request form, started on this plan and its budget band, with the person's email and the enterprise asking. */
function contactUrl(planId: string): string {
    const url = new URL(CONTACT_URL)
    url.searchParams.set('plan', planId)
    url.searchParams.set('budget', CONTACT_BUDGET)
    if (email.value) url.searchParams.set('email', email.value)
    if (enterprise.value) {
        url.searchParams.set('enterprise', enterprise.value.id)
        url.searchParams.set('slug', enterprise.value.slug)
    }
    return url.toString()
}

/**
 * Plan changes (os#1616) are for a live subscription, by whoever may write billing: the first checkout starts one.
 * Whether a plan is up or down is its place on the ladder, which the overview lists in order.
 */
const hasSubscription = computed(() => overview.value?.subscribed === true)
const canChangePlan = computed(() => canWrite.value && hasSubscription.value)
const ladder = computed(() => (overview.value?.plans ?? []).map(plan => plan.planId))
const currentRank = computed(() => ladder.value.indexOf(overview.value?.plan ?? ''))

type OfferedPlan = BillingOverview['plans'][number]

function isUpgrade(plan: OfferedPlan): boolean {
    return (
        canChangePlan.value &&
        currentRank.value >= 0 &&
        plan.selfServe &&
        ladder.value.indexOf(plan.planId) > currentRank.value
    )
}

function isDowngrade(plan: OfferedPlan): boolean {
    return (
        canChangePlan.value &&
        plan.selfServe &&
        plan.planId !== 'free' &&
        plan.planId !== pendingPlan.value?.planId &&
        ladder.value.indexOf(plan.planId) >= 0 &&
        ladder.value.indexOf(plan.planId) < currentRank.value
    )
}

/** Cancelling a paid plan: a move to Free at the period end, offered until one is pending. */
const canCancel = computed(
    () =>
        canChangePlan.value &&
        overview.value?.plan !== 'free' &&
        ladder.value.includes('free') &&
        pendingPlan.value?.planId !== 'free',
)

/** The lower plans whose previews the confirmations and the pending banner read. */
const previewTargets = computed(() => {
    const targets = (overview.value?.plans ?? []).filter(isDowngrade).map(plan => plan.planId)
    if (canCancel.value) targets.push('free')
    if (pendingPlan.value) targets.push(pendingPlan.value.planId)
    return [...new Set(targets)]
})

/** What the pending downgrade will change at the period end, item by item. */
const pendingItems = computed(() => (pendingPlan.value ? (previews.value[pendingPlan.value.planId]?.items ?? []) : []))

/**
 * When a move down lands, with its own preposition: "on 5 Nov 2026" when the period end is known, "at the end of the
 * period" when it is not, as the API's preview words it.
 */
function landsOn(at: string | null | undefined): string {
    return at ? `on ${billingDate(at)}` : 'at the end of the period'
}

/**
 * The confirmation for a move down to `planId`: when it lands, that nothing changes before, and every item the lower
 * plan cannot hold with what happens to it.
 */
function shrinkDescription(planId: string): string {
    const preview = previews.value[planId]
    const when = landsOn(preview?.effectiveAt)
    const pending = pendingPlan.value?.planId
    const replaces =
        pending && pending !== planId
            ? ` This replaces the move to ${planName(pending)} that is already scheduled.`
            : ''
    const head = `Moves to ${planName(planId)} ${when}.${replaces} Nothing changes before then, nothing is refunded, and you can withdraw it until then.`
    if (!preview) {
        return `${head} What it changes could not be read.`
    }
    if (preview.items.length === 0) {
        return `${head} Nothing you have is past ${planName(planId)}'s limits.`
    }
    return `${head} ${preview.items.map(item => `${item.detail}.`).join(' ')}`
}

function unitLabel(unit: string): string {
    return UNIT_LABEL[unit] ?? unit.replace(/_/g, ' ')
}

function planName(planId: string): string {
    return planId.charAt(0).toUpperCase() + planId.slice(1)
}

function count(amount: number): string {
    return amount.toLocaleString('en-US')
}

function usd(amount: number): string {
    return `$${count(amount)}`
}

/** A counted figure as a person reads it: whole where it is whole, else to two places ("0.16", never "0.1612903…"). */
function figure(amount: number): number {
    return Number(amount.toFixed(2))
}

function money(minor: number): string {
    return `$${(minor / 100).toFixed(2)}`
}

function day(value: string | null): string {
    return value ? value.slice(0, 10) : '—'
}

function expiry(method: BillingPaymentMethod): string {
    return `${String(method.expMonth).padStart(2, '0')}/${method.expYear}`
}

/** "visa ending 4242", or the method's own name where it is no card and has no digits ("PayPal"). */
function methodName(method: BillingPaymentMethod): string {
    return method.last4 ? `${method.brand} ending ${method.last4}` : method.brand
}

function failure(cause: unknown): string {
    return cause instanceof Error ? cause.message : String(cause)
}

function countryName(code: string | null): string {
    return code ? (BILLING_COUNTRIES.find(country => country.code === code)?.name ?? code) : '—'
}

/**
 * The business details on file, read back from FloPay each time (CTO decision 4). Read on its own, so FloPay being
 * unreachable costs this panel only, never the plan, usage or invoices.
 */
async function loadBusiness(): Promise<void> {
    businessError.value = ''
    try {
        business.value = (await api.billingBusinessDetails()).details
    } catch (cause) {
        business.value = null
        businessError.value = failure(cause)
    }
}

/**
 * Each part of the page is read on its own: invoices and payment methods come from FloPay, so FloPay failing costs
 * those parts and says why, never the plan and usage, which do not come from FloPay at all.
 */
async function load(): Promise<void> {
    loading.value = true
    error.value = ''
    try {
        const me = await api.me()
        canWrite.value = me?.isOwner === true || (me?.scopes ?? []).includes('billing:write')
        enterprise.value = me?.enterprise ? { id: me.enterprise.id, slug: me.enterprise.slug } : null
        const [nextOverview, nextInvoices, nextMethods, nextReconciliation] = await Promise.allSettled([
            api.billingOverview(),
            api.billingInvoices(),
            api.billingPaymentMethods(),
            api.billingReconciliation(),
        ])
        if (nextOverview.status === 'fulfilled') {
            overview.value = nextOverview.value
        }
        if (nextInvoices.status === 'fulfilled') {
            invoices.value = nextInvoices.value
        }
        if (nextMethods.status === 'fulfilled') {
            methods.value = nextMethods.value
        }
        if (nextReconciliation.status === 'fulfilled') {
            reconciliation.value = nextReconciliation.value
        }
        const failures = [nextOverview, nextInvoices, nextMethods, nextReconciliation].flatMap(result =>
            result.status === 'rejected' ? [failure(result.reason)] : [],
        )
        error.value = [...new Set(failures)].join(' ')
        await loadPlanChange()
    } catch (cause) {
        error.value = failure(cause)
    } finally {
        loading.value = false
    }
}

/**
 * The email the person signs in with, to start a checkout or a request on. Read on its own and never reported: with
 * none, the same links are offered without it, and the person types it where they land.
 */
async function loadEmail(): Promise<void> {
    try {
        const { identities } = await api.identities()
        email.value = identities.find(identity => identity.email)?.email ?? ''
    } catch {
        email.value = ''
    }
}

/** Read on its own, like every other part: a storage read that fails costs the storage meter only, and says why. */
async function loadStorage(): Promise<void> {
    try {
        storage.value = await api.storageUsage()
        storageError.value = ''
    } catch (cause) {
        storageError.value = failure(cause)
    }
}

/** Read on its own, like storage: a mail read that fails costs the Mail panel only, and says why. */
async function loadMail(): Promise<void> {
    try {
        mail.value = await api.mailUsage()
        mailError.value = ''
    } catch (cause) {
        mailError.value = failure(cause)
    }
}

async function loadSlots(): Promise<void> {
    try {
        const view = await api.concurrentSlots()
        slots.value = view
        slotsWanted.value = view.scheduled?.slots ?? view.requested ?? view.slots
        slotsLoadError.value = ''
    } catch (cause) {
        slotsLoadError.value = failure(cause)
    }
}

async function saveSlots(): Promise<void> {
    const view = slots.value
    if (!view?.canBuy || !canWrite.value || saving.value) return
    slotsError.value = ''
    slotsNote.value = ''
    const typed = String(slotsWanted.value).trim()
    const wanted = Number(typed)
    if (typed === '' || !Number.isInteger(wanted) || wanted < 0 || wanted > view.maxSlots) {
        slotsError.value = `Enter a whole number from 0 to ${view.maxSlots}.`
        return
    }
    saving.value = true
    try {
        slotsNote.value = noteFor(await api.setConcurrentSlots(wanted), view.slots)
        slots.value = await api.concurrentSlots()
    } catch (cause) {
        slotsError.value = failure(cause)
    } finally {
        saving.value = false
    }
}

/**
 * The pending downgrade and the previews of every lower plan on offer (os#1616), read once the overview says there is
 * a subscription to change. Read on their own: one that fails costs the plan change controls their detail and says
 * why, never the rest of the page.
 */
async function loadPlanChange(): Promise<void> {
    if (!hasSubscription.value) return
    planChangeError.value = ''
    try {
        pendingPlan.value = (await api.billingPlanChange()).pending
    } catch (cause) {
        pendingPlan.value = null
        planChangeError.value = failure(cause)
    }
    const read = await Promise.allSettled(previewTargets.value.map(planId => api.billingPlanChangePreview(planId)))
    const next: Record<string, PlanChangePreview> = {}
    for (const result of read) {
        if (result.status === 'fulfilled') {
            next[result.value.targetPlanId] = result.value
        } else if (!planChangeError.value) {
            planChangeError.value = failure(result.reason)
        }
    }
    previews.value = next
}

/**
 * The COO the plan always keeps active, as the server names it (the longest-serving `coo` holder): the members list is
 * sorted by name and carries no hire date, so it cannot tell which of two holders that is. Only an API older than
 * `cooMemberId` leaves it out, and then the first `coo` holder listed stands in.
 */
const keptCoo = computed(() => {
    const named = kept.value?.cooMemberId
    if (named !== undefined) return named ?? undefined
    return keptPeople.value.find(person => person.roles.some(role => role.slug === 'coo'))?.id
})
/** Whether the boxes checked fill what the plan keeps active, counting the COO. */
const keptFull = computed(() => (kept.value ? keptChecked.value.length >= kept.value.includedMembers : false))

/**
 * Who stays active and who is paused (os#1624), read on its own like every other part. Nothing to choose while no
 * member is paused, so the members are read only when some are.
 */
async function loadKeptMembers(): Promise<void> {
    try {
        const next = await api.billingKeptMembers()
        keptPeople.value = next.pausedMemberIds.length > 0 ? await api.members() : []
        kept.value = next
        resetKeptChecks()
        keptLoadError.value = ''
    } catch (cause) {
        keptLoadError.value = failure(cause)
    }
}

/** Checks every member the plan keeps active now, and the COO, who always is. */
function resetKeptChecks(): void {
    const paused = new Set(kept.value?.pausedMemberIds ?? [])
    keptChecked.value = keptPeople.value
        .filter(person => person.id === keptCoo.value || !paused.has(person.id))
        .map(person => person.id)
}

function keptBoxDisabled(person: Member): boolean {
    if (person.id === keptCoo.value || !canWrite.value || savingKept.value) return true
    return keptFull.value && !keptChecked.value.includes(person.id)
}

/** Checks or unchecks a member's box (the kit Checkbox's value; never indeterminate here). */
function toggleKept(person: Member, checked: boolean | 'indeterminate'): void {
    keptChecked.value =
        checked === true
            ? [...new Set([...keptChecked.value, person.id])]
            : keptChecked.value.filter(id => id !== person.id)
}

/** Saves the pick; a refusal says the server's words and leaves the boxes as they were set. */
async function saveKept(): Promise<void> {
    if (!canWrite.value || savingKept.value) return
    savingKept.value = true
    keptError.value = ''
    keptNote.value = ''
    try {
        const order = keptPeople.value.map(person => person.id)
        await api.billingKeepMembers(order.filter(id => id !== keptCoo.value && keptChecked.value.includes(id)))
        await loadKeptMembers()
        keptNote.value = 'Saved. The members checked run; the others stay paused until you upgrade or pick them.'
    } catch (cause) {
        keptError.value = failure(cause)
    } finally {
        savingKept.value = false
    }
}

/**
 * Asks for `planId`: up the ladder applies now and the plan moves once FloPay confirms the charge; down applies at
 * the period end; the plan the enterprise is on withdraws a pending downgrade. A refusal says FloPay's own words.
 */
async function changePlan(planId: string, cancel = false): Promise<void> {
    if (changingPlan.value) return
    changingPlan.value = true
    planChangeError.value = ''
    planChangeNote.value = ''
    try {
        const change = cancel ? await api.billingCancelPlan() : await api.billingChangePlan(planId)
        planChangeNote.value = planChangeNoteFor(change.effective, change.planId)
        await loadPlanChange()
    } catch (cause) {
        planChangeError.value = `${failure(cause)} Nothing was changed.`
    } finally {
        changingPlan.value = false
    }
}

function planChangeNoteFor(effective: 'now' | 'period_end' | 'unchanged', planId: string): string {
    if (effective === 'period_end') {
        return `Moving to ${planName(planId)} at the end of the period. Nothing changes until then.`
    }
    if (effective === 'now') {
        return planId === overview.value?.plan
            ? `Staying on ${planName(planId)}. The scheduled change is withdrawn.`
            : `Upgrade to ${planName(planId)} sent. Its limits apply as soon as FloPay confirms the charge.`
    }
    return 'Nothing to change.'
}

async function choosePlan(planId: string): Promise<void> {
    checkoutPlan.value = planId
    checkoutError.value = ''
    paymentMethodId.value = null
    fieldSession.value = null
    try {
        fieldSession.value = (await api.billingFieldSession()).sessionToken
    } catch (cause) {
        checkoutError.value = failure(cause)
    }
}

function closeCheckout(open: boolean): void {
    if (!open) {
        checkoutPlan.value = null
    }
}

async function checkout(details: CheckoutDetails): Promise<void> {
    if (!checkoutPlan.value) {
        return
    }
    if (!paymentMethodId.value) {
        checkoutError.value = 'Add a card in the payment fields to continue.'
        return
    }
    busy.value = true
    checkoutError.value = ''
    try {
        await api.billingCheckout({
            ...details,
            planId: checkoutPlan.value,
            interval: interval.value,
            paymentMethodId: paymentMethodId.value,
        })
        checkoutPlan.value = null
        notice.value = 'Checkout sent. Your plan changes as soon as FloPay confirms the payment.'
        await load()
    } catch (cause) {
        checkoutError.value = failure(cause)
    } finally {
        busy.value = false
    }
}

async function makeDefault(method: BillingPaymentMethod): Promise<void> {
    try {
        await api.billingMakeDefault(method.id)
        methods.value = await api.billingPaymentMethods()
    } catch (cause) {
        error.value = failure(cause)
    }
}

async function remove(method: BillingPaymentMethod): Promise<void> {
    try {
        await api.billingRemovePaymentMethod(method.id)
        methods.value = await api.billingPaymentMethods()
    } catch (cause) {
        error.value = failure(cause)
    }
}

/** Adds a card outside the first checkout, so one about to expire can be replaced: FloPay's fields, for this customer. */
async function addCard(): Promise<void> {
    addingCard.value = true
    cardError.value = ''
    cardSession.value = null
    try {
        cardSession.value = (await api.billingFieldSession()).sessionToken
    } catch (cause) {
        cardError.value = failure(cause)
    }
}

function closeAddCard(open: boolean): void {
    if (!open) {
        addingCard.value = false
    }
}

async function cardSaved(): Promise<void> {
    addingCard.value = false
    notice.value = 'Card added.'
    try {
        methods.value = await api.billingPaymentMethods()
    } catch (cause) {
        error.value = failure(cause)
    }
}

/**
 * A plan named in the address (`/billing?plan=starter&interval=annual`), the way a pricing page's Sign up for a paid
 * plan arrives here, through sign-in, which keeps the address it was asked for. Once the plans are read, that plan's
 * card does what its Choose plan would, at the interval named: FloPay's hosted page, signed for the enterprise and
 * started on the person's email, or the in-portal checkout where the deployment links none. The address is cleared
 * first, so a reload or a closed dialog asks nothing again. A plan that cannot be chosen here (the current one, one priced by hand, a
 * subscription already live, a person who may not buy) opens nothing: the plans show as they always do.
 */
function openPlanFromAddress(): void {
    const { plan, interval: at, ...rest } = route.query
    if (typeof plan !== 'string') return
    void router.replace({ query: rest })
    annual.value = at === 'annual'
    const card = planCards.value.find(candidate => candidate.planId === plan)
    if (!card?.choose) return
    if (card.hosted) {
        window.location.assign(card.hosted)
    } else {
        void choosePlan(card.planId)
    }
}

/**
 * `/billing#plans`, where every Upgrade in the portal points: once the plans are on the page, it scrolls down to them
 * instead of opening at its top. Followed again when the hash changes on this page, as a banner's Upgrade does here.
 */
async function scrollToPlansWhenAsked(): Promise<void> {
    if (route.hash !== '#plans') return
    await nextTick()
    document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

watch(
    () => route.hash,
    () => void scrollToPlansWhenAsked(),
)

onMounted(() => {
    // The plan named in the address waits for the email too, so FloPay's page opens already on it.
    const emailRead = loadEmail()
    void load()
        .then(() => emailRead)
        .then(openPlanFromAddress)
        .then(scrollToPlansWhenAsked)
    void loadBusiness()
    void loadStorage()
    void loadMail()
    void loadSlots()
    void loadKeptMembers()
})
</script>

<template>
    <div class="grid w-full gap-6">
        <ZhPageTitle kicker="SETTINGS">
            Billing
            <template v-if="overview" #meta>
                {{ planName(overview.plan) }} plan
                <template v-if="overview.interval">· {{ BILLED[overview.interval] }}</template>
                <template v-if="overview.periodStart">· {{ day(overview.periodStart) }} to {{ day(overview.periodEnd) }}</template>
                <template v-if="overview.pastDue">· Payment past due</template>
                <template v-if="overview.cancelAt">· Ends {{ day(overview.cancelAt) }}</template>
            </template>
        </ZhPageTitle>

        <p v-if="problems" role="alert" class="text-sm text-destructive">{{ problems }}</p>
        <p v-if="notice" role="status" class="text-sm text-muted-foreground">{{ notice }}</p>
        <p v-if="loading" class="text-sm text-muted-foreground">Loading billing…</p>

        <ZhPanel v-for="said in subscriptionNotices" :key="said.key" tone="action" role="status" :data-notice="said.key">
            <p class="text-sm">{{ said.text }}</p>
        </ZhPanel>

        <Dialog :open="checkoutPlan !== null" @update:open="closeCheckout">
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{{ checkoutPlan ? planName(checkoutPlan) : '' }} plan</DialogTitle>
                    <DialogDescription>
                        {{ BILLED[interval] }}. Your plan changes as soon as FloPay confirms the payment.
                    </DialogDescription>
                </DialogHeader>
                <div class="flex items-center gap-2">
                    <Switch id="billing-annual" v-model="annual" />
                    <Label for="billing-annual">Bill annually</Label>
                </div>
                <p v-if="checkoutError" role="alert" class="text-sm text-destructive">{{ checkoutError }}</p>
                <BillingCheckoutForm
                    :countries="[...BILLING_COUNTRIES]"
                    :tax-id-countries="[...TAX_ID_COUNTRIES]"
                    :field-session="fieldSession"
                    :busy="busy"
                    @payment-method="paymentMethodId = $event"
                    @submit="checkout"
                />
            </DialogContent>
        </Dialog>

        <Dialog :open="addingCard" @update:open="closeAddCard">
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Add a card</DialogTitle>
                    <DialogDescription>
                        Enter the card in FloPay's secure fields. Once saved it is listed here, ready to make the default.
                    </DialogDescription>
                </DialogHeader>
                <p v-if="cardError" role="alert" class="text-sm text-destructive">{{ cardError }}</p>
                <BillingCardFields :field-session="cardSession" @payment-method="cardSaved" />
            </DialogContent>
        </Dialog>

        <ZhPanel v-if="meters.length > 0" as="section" aria-label="Usage">
            <ZhSectionHeading>Usage this period</ZhSectionHeading>
            <div class="mt-4 grid gap-4 md:grid-cols-3">
                <ZhUsageMeter
                    v-for="meter in meters"
                    :key="meter.key"
                    :used="meter.used"
                    :included="meter.included"
                    :unit="meter.unit"
                />
            </div>
            <p v-if="memberMonthsLine" class="mt-4 text-xs text-muted-foreground">{{ memberMonthsLine }}</p>
            <p v-if="concurrencyLine" class="mt-4 text-xs text-muted-foreground">{{ concurrencyLine }}</p>
            <p v-if="storage" class="mt-4 text-xs text-muted-foreground">
                Storage is run artifacts, task files, chat files and mail. Storage past what the plan includes is billed by the GB-month where the plan allows it; on Free, new runs stop once it is full, and nothing already stored is deleted.
            </p>
        </ZhPanel>

        <ZhPanel v-if="mail" as="section" aria-label="Mail">
            <ZhSectionHeading>Mail</ZhSectionHeading>
            <div class="mt-4 grid gap-1">
                <p
                    v-for="line in mailLines"
                    :key="line.memberId"
                    class="text-sm"
                    :class="{ 'text-destructive': line.full }"
                >
                    {{ line.text }}
                </p>
                <p v-if="mailLines.length === 0" class="text-sm">No member mailboxes yet.</p>
            </div>
            <p v-if="mailRetentionLine" class="mt-4 text-sm">{{ mailRetentionLine }}</p>
            <p class="mt-4 text-xs text-muted-foreground">
                The daily limit on incoming mail applies per mailbox and resets at 00:00 UTC. Mail over it is held, never bounced, and arrives oldest first as the daily limit frees up, or at once when the plan goes up.
            </p>
        </ZhPanel>

        <ZhPanel v-if="slots" as="section" aria-label="Concurrent runs">
            <ZhSectionHeading>Concurrent runs</ZhSectionHeading>
            <p class="mt-4 text-sm">{{ ceilingLine }}</p>
            <div v-if="slots.canBuy" class="mt-2 grid gap-2">
                <p class="text-xs text-muted-foreground">
                    ${{ slots.slotPriceUsd }} per extra concurrent run a month on {{ slots.planName }}. {{ slots.slots }} extra bought. More apply straight away; fewer apply at the end of the period. Nothing is added unless you set it here.
                </p>
                <p v-if="requestedLine" class="text-sm">{{ requestedLine }}</p>
                <p v-if="scheduledLine" class="text-sm">{{ scheduledLine }}</p>
                <template v-if="canWrite">
                    <div class="flex flex-wrap items-end gap-2">
                        <div class="grid gap-1">
                            <Label for="concurrent-slots">Extra concurrent runs</Label>
                            <Input id="concurrent-slots" v-model="slotsWanted" type="number" min="0" :max="slots.maxSlots" step="1" :disabled="saving" />
                        </div>
                        <Button type="button" :disabled="saving" @click="saveSlots">Save</Button>
                    </div>
                    <p v-if="slotsError" role="alert" class="text-sm text-destructive">{{ slotsError }}</p>
                    <p v-if="slotsNote" class="text-xs text-muted-foreground">{{ slotsNote }}</p>
                </template>
            </div>
            <p v-else class="mt-1 text-xs text-muted-foreground">
                The {{ slots.planName }} plan sells no extra concurrent runs. Upgrade to a paid plan to buy them.
            </p>
        </ZhPanel>

        <template v-if="overview">
            <ZhPanel v-if="pendingPlan" as="section" aria-label="Pending plan change">
                <ZhBanner>
                    Moving to {{ planName(pendingPlan.planId) }} {{ landsOn(pendingPlan.from) }}. Nothing changes until then.
                    <template v-if="canChangePlan" #action>
                        <ZhConfirmButton
                            label="Withdraw"
                            :title="`Stay on ${planName(overview.plan)}?`"
                            :description="`The move to ${planName(pendingPlan.planId)} is withdrawn, and the ${planName(overview.plan)} plan carries on as it is. Nothing else changes.`"
                            :confirm-label="`Stay on ${planName(overview.plan)}`"
                            :disabled="changingPlan"
                            @confirm="changePlan(overview.plan)"
                        />
                    </template>
                </ZhBanner>
                <ZhSectionHeading class="mt-4">What changes then</ZhSectionHeading>
                <dl v-if="pendingItems.length > 0" class="mt-2">
                    <ZhKeyValue v-for="item in pendingItems" :key="item.unit" :label="PREVIEW_UNIT[item.unit] ?? item.unit">
                        <ZhStatusPill :tone="OUTCOME[item.outcome].tone" :pulse="false">{{ OUTCOME[item.outcome].word }}</ZhStatusPill>
                        {{ item.detail }}
                    </ZhKeyValue>
                </dl>
                <p v-else class="mt-2 text-sm text-muted-foreground">
                    Nothing you have is past {{ planName(pendingPlan.planId) }}'s limits.
                </p>
            </ZhPanel>

            <ZhPanel id="plans" as="section" aria-label="Plans" class="scroll-mt-20">
                <div class="flex flex-wrap items-center justify-between gap-3">
                    <ZhSectionHeading>Plans</ZhSectionHeading>
                    <div class="flex flex-wrap items-center gap-4">
                        <div v-if="sellsAnnual" class="flex items-center gap-2">
                            <Switch id="billing-plans-annual" v-model="annual" />
                            <Label for="billing-plans-annual">Bill annually</Label>
                        </div>
                        <a :href="PRICING_URL" target="_blank" rel="noopener" class="text-sm underline">
                            Compare plans in full
                        </a>
                    </div>
                </div>
                <p v-if="planChangeError" role="alert" class="mt-4 text-sm text-destructive">{{ planChangeError }}</p>
                <p v-if="changingPlan" role="status" class="mt-4 text-sm text-muted-foreground">
                    Sending the plan change to FloPay, our payment provider…
                </p>
                <p v-else-if="planChangeNote" role="status" class="mt-4 text-sm text-muted-foreground">{{ planChangeNote }}</p>
                <ZhCarousel label="Plans" class="mt-4">
                    <ZhPlanCard
                        v-for="plan in planCards"
                        :key="plan.planId"
                        as="li"
                        :name="plan.name"
                        :tagline="plan.tagline"
                        :price="plan.price"
                        :per="plan.per"
                        :current="plan.current"
                        :features="plan.features"
                    >
                        <template v-if="plan.choose || plan.upgrade || plan.downgrade || plan.cancel || plan.talk" #action>
                            <template v-if="plan.choose">
                                <Button v-if="plan.hosted" as-child size="sm">
                                    <a :href="plan.hosted">Choose plan</a>
                                </Button>
                                <Button v-else size="sm" @click="choosePlan(plan.planId)">Choose plan</Button>
                            </template>
                            <ZhButton v-else-if="plan.upgrade" :disabled="changingPlan" @click="changePlan(plan.planId)">
                                Upgrade to {{ planName(plan.planId) }}
                            </ZhButton>
                            <ZhConfirmButton
                                v-else-if="plan.downgrade"
                                :label="`Downgrade to ${planName(plan.planId)}`"
                                :title="`Move to ${planName(plan.planId)} at the end of the period?`"
                                :description="shrinkDescription(plan.planId)"
                                :disabled="changingPlan || !previews[plan.planId]"
                                @confirm="changePlan(plan.planId)"
                            />
                            <ZhConfirmButton
                                v-else-if="plan.cancel"
                                label="Cancel plan"
                                :title="`Cancel the ${planName(overview.plan)} plan?`"
                                :description="shrinkDescription('free')"
                                confirm-label="Cancel at the period end"
                                :disabled="changingPlan || !previews.free"
                                @confirm="changePlan('free', true)"
                            />
                            <Button v-else-if="plan.talk" as-child size="sm" variant="outline">
                                <a :href="plan.talk" target="_blank" rel="noopener">Talk to us</a>
                            </Button>
                        </template>
                    </ZhPlanCard>
                </ZhCarousel>
            </ZhPanel>

            <!-- os#1624: after a move to Free, the owner picks who stays active. Shown only while members are paused. -->
            <ZhPanel v-if="kept && kept.pausedMemberIds.length > 0" as="section" aria-label="Choose who stays active">
                <ZhSectionHeading>Choose who stays active</ZhSectionHeading>
                <p class="mt-2 text-sm text-muted-foreground">
                    {{ planName(overview.plan) }} runs {{ kept.includedMembers }} AI members, counting your COO. The rest are paused, never deleted, and run again as soon as you upgrade; members past what a paid plan includes are billed at its overage rate.
                </p>
                <p v-if="keptFull" class="mt-2 text-sm">
                    Your {{ planName(overview.plan) }} plan keeps {{ kept.includedMembers }} active, counting your COO.
                </p>
                <p v-if="keptError" role="alert" class="mt-2 text-sm text-destructive">{{ keptError }}</p>
                <p v-else-if="keptNote" role="status" class="mt-2 text-sm text-muted-foreground">{{ keptNote }}</p>
                <ul class="mt-4 grid gap-2">
                    <li v-for="person in keptPeople" :key="person.id" class="flex flex-wrap items-center gap-3 text-sm">
                        <label class="flex items-center gap-2">
                            <Checkbox
                                :model-value="keptChecked.includes(person.id)"
                                :disabled="keptBoxDisabled(person)"
                                @update:model-value="toggleKept(person, $event)"
                            />
                            {{ memberLabel(person) }}
                        </label>
                        <ZhStatusPill :tone="kept.pausedMemberIds.includes(person.id) ? 'action' : 'running'" :pulse="false">
                            {{ kept.pausedMemberIds.includes(person.id) ? 'Paused' : 'Active' }}
                        </ZhStatusPill>
                    </li>
                </ul>
                <Button v-if="canWrite" type="button" class="mt-4" size="sm" :disabled="savingKept" @click="saveKept">
                    {{ savingKept ? 'Saving…' : 'Save' }}
                </Button>
                <p v-else class="mt-4 text-sm text-muted-foreground">Only someone who can change billing can choose who stays active.</p>
            </ZhPanel>

            <ZhPanel v-if="business || businessError">
                <ZhSectionHeading>Business details</ZhSectionHeading>
                <p v-if="businessError" role="alert" class="mt-4 text-sm text-destructive">{{ businessError }}</p>
                <dl v-else-if="business" class="mt-4">
                    <ZhKeyValue label="Legal name">{{ business.legalName }}</ZhKeyValue>
                    <ZhKeyValue label="Address">{{ business.address ?? '—' }}</ZhKeyValue>
                    <ZhKeyValue label="Country">{{ countryName(business.country) }}</ZhKeyValue>
                    <ZhKeyValue label="Tax ID">{{ business.taxId ?? '—' }}</ZhKeyValue>
                </dl>
            </ZhPanel>

            <ZhPanel v-if="showsPaymentMethods" as="section" aria-label="Payment methods">
                <div class="flex flex-wrap items-center justify-between gap-3">
                    <ZhSectionHeading>Payment methods</ZhSectionHeading>
                    <Button v-if="canAddCard" size="sm" variant="outline" @click="addCard">Add a card</Button>
                </div>
                <ZhEmptyState v-if="methods.length === 0" compact label="NO CARD">
                    No card on file yet.
                </ZhEmptyState>
                <ul v-else class="mt-4 grid gap-2">
                    <li v-for="method in methods" :key="method.id" class="flex flex-wrap items-center gap-3">
                        <span class="font-medium">{{ methodName(method) }}</span>
                        <span v-if="method.expMonth" class="text-sm text-muted-foreground">expires {{ expiry(method) }}</span>
                        <span v-if="method.isDefault" class="text-sm text-muted-foreground">(default)</span>
                        <template v-if="canWrite">
                            <Button v-if="canMakeDefault && !method.isDefault" size="sm" variant="outline" @click="makeDefault(method)">
                                Make default
                            </Button>
                            <ZhConfirmButton
                                label="Remove"
                                :title="`Remove ${method.last4 ? `the ${method.brand} card ending ${method.last4}` : method.brand}?`"
                                description="FloPay stops holding this card and can no longer charge it. To use it again, add it again."
                                confirm-label="Remove card"
                                :aria-label="`Remove ${methodName(method)}`"
                                @confirm="remove(method)"
                            />
                        </template>
                    </li>
                </ul>
            </ZhPanel>

            <ZhPanel>
                <ZhSectionHeading>Invoices</ZhSectionHeading>
                <ZhEmptyState v-if="invoices.length === 0" compact label="NO INVOICES">
                    Invoices appear here after the first charge.
                </ZhEmptyState>
                <Table v-else>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Invoice</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Total</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Document</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        <TableRow v-for="invoice in invoices" :key="invoice.id">
                            <TableCell>{{ invoice.number }}</TableCell>
                            <TableCell>{{ day(invoice.date) }}</TableCell>
                            <TableCell>{{ money(invoice.total) }}</TableCell>
                            <TableCell>{{ invoice.status }}</TableCell>
                            <TableCell>
                                <a v-if="invoice.documentUrl" :href="invoice.documentUrl" target="_blank" rel="noopener" class="underline">PDF</a>
                                <template v-else>—</template>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </ZhPanel>

            <ZhPanel v-if="reconciliation.length > 0">
                <ZhSectionHeading>Closed periods</ZhSectionHeading>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Period</TableHead>
                            <TableHead>Unit</TableHead>
                            <TableHead>Recorded</TableHead>
                            <TableHead>Included</TableHead>
                            <TableHead>Reported</TableHead>
                            <TableHead>Status</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        <TableRow v-for="row in reconciliation" :key="`${row.periodStart}-${row.unit}`">
                            <TableCell>{{ day(row.periodStart) }} to {{ day(row.periodEnd) }}</TableCell>
                            <TableCell>{{ unitLabel(row.unit) }}</TableCell>
                            <TableCell>{{ figure(row.recorded) }}</TableCell>
                            <TableCell>{{ row.included ?? 'Unlimited' }}</TableCell>
                            <TableCell>{{ row.reported === null ? '—' : figure(row.reported) }}</TableCell>
                            <TableCell>{{ row.status }}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </ZhPanel>

            <ZhPanel as="section" aria-label="Export" data-testid="billing-export">
                <ZhSectionHeading>Export</ZhSectionHeading>
                <p class="mt-4 text-sm text-muted-foreground">
                    Download a closed period's usage, per meter and per member, and the invoices, as CSV files for finance.
                    A period runs from its first day up to, not including, its end day.
                </p>
                <template v-if="exportPeriod">
                    <div class="mt-4 flex flex-wrap items-end gap-3">
                        <div class="grid gap-2">
                            <Label id="billing-export-period-label">Period</Label>
                            <Select :model-value="exportPeriod.start" @update:model-value="pickExportPeriod">
                                <SelectTrigger class="w-64" aria-labelledby="billing-export-period-label">
                                    <SelectValue>{{ exportPeriod.label }}</SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem v-for="period in exportPeriods" :key="period.start" :value="period.start">
                                        {{ period.label }}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <Button as-child size="sm" variant="outline">
                            <a data-testid="billing-export-usage" :href="billingUsageExportUrl(exportPeriod.start)" download>Usage for this period (CSV)</a>
                        </Button>
                        <template v-if="invoices.length > 0">
                            <Button as-child size="sm" variant="outline">
                                <a data-testid="billing-export-invoices" :href="billingInvoicesExportUrl(exportPeriod.start)" download>Invoices dated in this period (CSV)</a>
                            </Button>
                            <Button as-child size="sm" variant="ghost">
                                <a data-testid="billing-export-invoices-all" :href="billingInvoicesExportUrl()" download>All invoices (CSV)</a>
                            </Button>
                        </template>
                    </div>
                    <div class="mt-3 grid gap-1 text-sm text-muted-foreground" data-testid="billing-export-contents">
                        <p>
                            Usage: one row per meter, then one row per member and unit. Units are named as the API names them
                            (member_month, member_day, runner_minute, portal_run, storage_gb), and dates are UTC.
                        </p>
                        <p>
                            Invoices: one row per invoice, newest first, dated as the Invoices list dates them. The invoice that
                            bills a period's overage is raised as the period ends, so it is usually listed under the next period.
                        </p>
                        <p v-if="invoices.length > 0 && !invoicesDatedIn(exportPeriod)" data-testid="billing-export-no-invoices">
                            No invoice is dated in this period, so its invoices file holds the header row only.
                        </p>
                    </div>
                </template>
                <template v-else>
                    <div data-testid="billing-export-empty">
                        <ZhEmptyState compact label="NOTHING TO EXPORT YET">
                            A period's usage can be exported once the period has closed.
                        </ZhEmptyState>
                    </div>
                    <Button v-if="invoices.length > 0" as-child size="sm" variant="outline" class="mt-4">
                        <a data-testid="billing-export-invoices" :href="billingInvoicesExportUrl()" download>All invoices (CSV)</a>
                    </Button>
                </template>
            </ZhPanel>
        </template>
    </div>
</template>
